import React from "react";
import {
  createStyles,
  lighten,
  makeStyles,
  Theme,
} from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Checkbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteIcon from "@material-ui/icons/Delete";
import FilterListIcon from "@material-ui/icons/FilterList";
import SkeletonCustomTable from "./SkeletonCustomTable";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { Collapse } from "@material-ui/core";
import TableErrorMessage from "./TableErrorMessage";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      "& .MuiTableRow-root:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      "& .Mui-selected": {
        backgroundColor: theme.palette.action.selected,
      },
      "& .Mui-selected:hover": {
        backgroundColor: theme.palette.action.focus,
      },
    },
    paper: {
      width: "100%",
      marginBottom: theme.spacing(2),
    },
    table: {
      minWidth: 750,
    },
    visuallyHidden: {
      border: 0,
      clip: "rect(0 0 0 0)",
      height: 1,
      margin: -1,
      overflow: "hidden",
      padding: 0,
      position: "absolute",
      top: 20,
      width: 1,
    },
    head: {
      "& .MuiTableCell-stickyHeader": {
        backgroundColor: "white",
        height: 60,
      },
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  // const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  // stabilizedThis.sort((a, b) => {
  //   const order = comparator(a[0], b[0]);
  //   if (order !== 0) return order;
  //   return a[1] - b[1];
  // });
  // return stabilizedThis.map((el) => el[0]);
  return array;
}

export interface RowItemType extends Record<string, any> {}

export interface HeadCell {
  disablePadding: boolean;
  id: keyof RowItemType;
  //id: String;
  label: string;
  numeric: boolean;
  colspan?: number;
  hidden?: boolean;
  width?: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof RowItemType
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: any;
  rowCount: number;
  headers: HeadCell[];
  isSelectable?: boolean;
  isCollapsible?: boolean;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    classes,
    onSelectAllClick,
    // order,
    // orderBy,
    numSelected,
    rowCount,
    // onRequestSort,
    headers,
    isSelectable,
    isCollapsible,
  } = props;
  // const createSortHandler =
  //   (property: keyof RowItemType) => (event: React.MouseEvent<unknown>) => {
  //     onRequestSort(event, property);
  //   };

  return (
    <TableHead className={classes.head}>
      <TableRow>
        {isCollapsible === true && <TableCell colSpan={1} />}
        {isSelectable === true && (
          <TableCell padding="checkbox" colSpan={1}>
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{ "aria-label": "select all desserts" }}
            />
          </TableCell>
        )}
        {headers.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"center"}
            padding={headCell.disablePadding ? "none" : "normal"}
            style={{
              fontWeight: "bold",
              fontSize: "8pt",
              lineHeight: "10pt",
              letterSpacing: "1px",
              display: Boolean(
                headCell.label.toLowerCase() === "selected" ||
                  headCell.hidden === true
              )
                ? "none"
                : "table-cell",
            }}
          >
            {/* <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            > */}
            {headCell.label.toUpperCase()}
            {/* {orderBy === headCell.id ? (
              <span className={classes.visuallyHidden}>
                {order === "desc" ? "sorted descending" : "sorted ascending"}
              </span>
            ) : null} */}
            {/* </TableSortLabel> */}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
    },
    highlight:
      theme.palette.type === "light"
        ? {
            color: theme.palette.primary.main,
            backgroundColor: lighten(theme.palette.primary.light, 0.85),
          }
        : {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.primary.dark,
          },
    title: {
      flex: "1 1 100%",
    },
  })
);

interface EnhancedTableToolbarProps {
  numSelected: number;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const classes = useToolbarStyles();
  const { numSelected } = props;

  return (
    <Toolbar>
      {numSelected > 0 ? (
        <Typography
          className={classes.title}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} Seleccionados
        </Typography>
      ) : (
        <Typography
          className={classes.title}
          variant="h6"
          id="tableTitle"
          component="div"
        ></Typography>
      )}
      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton aria-label="delete">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton aria-label="filter list">
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
};

interface props {
  rows: RowItemType[];
  headers: HeadCell[];
  isLoading?: boolean;
  isError?: boolean;
  totalRecords: number;
  page: number;
  rowsPerPage: number;
  onChangePage: (page: number) => void;
  onChangeRowsPerPage: (rowsPerPage: number) => void;
  isSelectable?: boolean;
  isCollapsible?: boolean;
  CollapseContent?: JSX.Element[];
  hideFooter?: boolean;
  dense?: boolean;
}

interface rowProp {
  row: RowItemType;
  countHeaders: number;
  isCollapsible?: boolean;
  CollapseContent?: JSX.Element;
  headers: HeadCell[];
}

const useRowStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "& > *": {
        borderBottom: "unset",
        width: "20px",
      },
    },
  })
);

const RenderCells = ({
  row,
  countHeaders,
  isCollapsible,
  CollapseContent,
  headers,
}: rowProp) => {
  const [open, setOpen] = React.useState(false);
  const classes = useRowStyles();

  return (
    <>
      <TableRow className={classes.root} selected={row.selected === true}>
        {isCollapsible === true && (
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <IconChevronUp /> : <IconChevronDown />}
            </IconButton>
          </TableCell>
        )}
        {Object.entries(row).map(([key, val], index) => {
          const head = headers[index];
          if (head === undefined) return <></>;
          return (
            <TableCell
              key={index}
              align={"center"}
              style={{
                display: Boolean(
                  String(key).toLowerCase() === "selected" ||
                    head.hidden === true
                )
                  ? "none"
                  : "table-cell",
              }}
            >
              <div
                style={{
                  width: head.width || "auto",
                  margin: "0 auto",
                }}
              >
                {val}
              </div>
            </TableCell>
          );
        })}
      </TableRow>
      <TableRow>
        <TableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
            padding: 0,
          }}
          colSpan={countHeaders}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <div
              style={{
                padding: "1rem",
                backgroundColor: "rgba(224, 224, 224, .65)",
              }}
            >
              {CollapseContent}
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const CustomTable = ({
  rows,
  headers,
  isLoading,
  isError,
  totalRecords,
  page,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  isSelectable,
  isCollapsible,
  CollapseContent,
  hideFooter,
  dense,
}: props) => {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof RowItemType>("id");
  const [selected, setSelected] = React.useState<String[]>([]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof RowItemType
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  /* const handleClick = (event: React.MouseEvent<unknown>, name: String) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: String[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  }; */

  const handleChangePage = (event: unknown, newPage: number) => {
    //setPage(newPage);
    onChangePage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChangeRowsPerPage(parseInt(event.target.value, 10));
  };

  // const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setDense(event.target.checked);
  // };

  // const isSelected = (name: String) => selected.indexOf(name) !== -1;

  // const emptyRows =
  //   rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  if (isLoading) {
    return <SkeletonCustomTable cellsCount={headers.length} rowsCount={10} />;
  }

  if (isError === true) {
    return <TableErrorMessage />;
  }

  return (
    <div className={classes.root}>
      <Paper elevation={0} variant="outlined" className={classes.paper}>
        {isSelectable && <EnhancedTableToolbar numSelected={selected.length} />}
        <TableContainer
          style={{
            maxHeight: 500,
          }}
        >
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={dense === true ? "small" : "medium"}
            aria-label="enhanced table"
            stickyHeader
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              headers={headers}
              isSelectable={isSelectable}
              isCollapsible={isCollapsible}
            />
            <TableBody>
              {stableSort(rows, getComparator(order, orderBy))
                //.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  //const isItemSelected = isSelected(String(row.id));
                  return (
                    <RenderCells
                      key={index}
                      row={row}
                      countHeaders={
                        headers.length + (isCollapsible === true ? 1 : 0)
                      }
                      isCollapsible={isCollapsible}
                      CollapseContent={
                        CollapseContent && CollapseContent[index]
                      }
                      headers={headers}
                    />
                  );
                })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={
                      headers.length +
                      (headers.find((head) => head.colspan)?.colspan || 0)
                    }
                  >
                    <Typography style={{ textAlign: "center" }}>
                      No hay datos disponibles
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!hideFooter && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={(info) => (
              <span>
                {info.from}&nbsp;-&nbsp;{info.to}&nbsp;de&nbsp;{info.count}
                &nbsp;registros
              </span>
            )}
          />
        )}
      </Paper>
    </div>
  );
};

export default CustomTable;
